import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSoftphone } from '../store/softphone';
import { formatUKForDisplay } from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import { api } from '../lib/api';

interface LeadCallContext {
  lastCall: {
    id: string;
    startedAt: string;
    disposition: string | null;
    notes: string | null;
    durationSeconds: number | null;
    analysis: {
      id: string;
      summary: string | null;
      sentimentOverall: string | null;
      suggestedDisposition: string | null;
      semanticExtraction: unknown;
      executiveSynthesis: unknown;
    } | null;
  } | null;
}

export function LeadContextPanel(): JSX.Element | null {
  const lead = useSoftphone((s) => s.currentLead);
  const currentCall = useSoftphone((s) => s.currentCall);
  const setLead = useSoftphone((s) => s.setLead);
  const context = useQuery({
    queryKey: ['lead-call-context', lead?.id, currentCall?.id],
    queryFn: () => api.get<LeadCallContext>(
      `/leads/${lead!.id}/call-context${currentCall?.id ? `?excludeCallId=${encodeURIComponent(currentCall.id)}` : ''}`,
    ),
    enabled: !!lead,
    staleTime: 30_000,
  });
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteMsg, setNoteMsg] = useState<string | null>(null);

  const submitNote = async (): Promise<void> => {
    const text = noteDraft.trim();
    if (!text || !lead) return;
    setNoteBusy(true);
    setNoteMsg(null);
    try {
      const res = await api.post<{ syncedToPerfex: boolean }>(`/leads/${lead.id}/notes`, { body: text });
      setLead({ ...lead, lastNote: text });
      setNoteDraft('');
      setNoteMsg(res.syncedToPerfex ? '✓ Saved & synced to ho.marn.uk' : '✓ Saved (no CRM record linked yet)');
    } catch {
      setNoteMsg('Could not save note');
    } finally {
      setNoteBusy(false);
    }
  };

  if (!lead) return null;

  const customFields = lead.customFields ?? {};
  const customEntries = Object.entries(customFields).filter(([, v]) => v != null);

  return (
    <div className="card p-4 space-y-3">
      <header>
        <div className="text-xs uppercase tracking-wide text-ink-500">Lead context</div>
        <div className="text-base font-semibold">
          {lead.firstName} {lead.lastName}
        </div>
        {lead.company && <div className="text-sm text-ink-600">{lead.company}</div>}
      </header>
      <dl className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-ink-500">Phone</dt>
          <dd className="font-mono">{formatUKForDisplay(lead.phoneE164)}</dd>
        </div>
        {lead.email && (
          <div className="flex justify-between">
            <dt className="text-ink-500">Email</dt>
            <dd className="truncate ml-2">{lead.email}</dd>
          </div>
        )}
        {lead.temperature && (
          <div className="flex justify-between">
            <dt className="text-ink-500">Temperature</dt>
            <dd className="capitalize">{lead.temperature}</dd>
          </div>
        )}
        {lead.lastContactAt && (
          <div className="flex justify-between">
            <dt className="text-ink-500">Last contact</dt>
            <dd>{relativeTime(lead.lastContactAt)}</dd>
          </div>
        )}
      </dl>
      {lead.lastNote && (
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">Last note</div>
          <p className="text-sm leading-relaxed">{lead.lastNote}</p>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">Add note</div>
        <textarea
          className="input min-h-[64px] text-sm"
          placeholder="Add a note — it will also appear on this record in ho.marn.uk…"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-[11px] text-ink-500">{noteMsg}</span>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={submitNote}
            disabled={noteBusy || !noteDraft.trim()}
          >
            {noteBusy ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>
      {context.data?.lastCall && (
        <div className="rounded-md border border-ink-200 bg-ink-50 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-wide text-ink-500">Last call</div>
            <span className="text-xs text-ink-500">{relativeTime(context.data.lastCall.startedAt)}</span>
          </div>
          {context.data.lastCall.disposition && (
            <span className="badge bg-white text-ink-700">{context.data.lastCall.disposition.replace(/_/g, ' ')}</span>
          )}
          {context.data.lastCall.notes && (
            <p className="text-sm leading-relaxed">{context.data.lastCall.notes}</p>
          )}
          {context.data.lastCall.analysis && (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs uppercase tracking-wide text-ink-500">AI</span>
                {context.data.lastCall.analysis.sentimentOverall && (
                  <span className="badge bg-white text-ink-700">{context.data.lastCall.analysis.sentimentOverall}</span>
                )}
                {context.data.lastCall.analysis.suggestedDisposition && (
                  <span className="badge bg-white text-ink-700">
                    {context.data.lastCall.analysis.suggestedDisposition.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              {analysisSummary(context.data.lastCall.analysis) && (
                <p className="text-sm leading-relaxed">{analysisSummary(context.data.lastCall.analysis)}</p>
              )}
              {recommendedAction(context.data.lastCall.analysis.executiveSynthesis) && (
                <p className="text-xs leading-relaxed text-emerald-800">
                  Next: {recommendedAction(context.data.lastCall.analysis.executiveSynthesis)}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {customEntries.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">Custom fields</div>
          <ul className="text-sm space-y-0.5">
            {customEntries.map(([key, value]) => (
              <li key={key} className="flex justify-between">
                <span className="text-ink-500">{key}</span>
                <span>{String(value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function analysisSummary(analysis: NonNullable<LeadCallContext['lastCall']>['analysis']): string | null {
  if (!analysis) return null;
  return analysis.summary ?? executiveString(analysis.executiveSynthesis, 'executiveSummary');
}

function recommendedAction(executiveSynthesis: unknown): string | null {
  return executiveString(executiveSynthesis, 'recommendedNextAction');
}

function executiveString(value: unknown, key: string): string | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = (value as Record<string, unknown>)[key];
  return typeof candidate === 'string' && candidate.trim() ? candidate : null;
}
